import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useLeonLoading } from '../components/LeonLoadingSystem';
import { 
  BookOpen, 
  Loader2,
  Clock,
  Wrench,
  Package,
  AlertTriangle,
  Shield,
  ListOrdered,
  FileText,
  Printer,
  CheckSquare,
  Square,
  Lightbulb,
  Camera,
  X,
  Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import LeonMascot from '../components/LeonMascot';
import MobilePhotoUpload from '../components/MobilePhotoUpload';
import { LeonTutorial, TutorialWelcomeCard, useTutorial } from '../components/LeonTutorial';
import { LeadCaptureCTA } from '../components/LeadCapture';
import { LeadInterestPopup } from '../components/LeadPopup';
import { useAdGate, BannerAd } from '../components/AdSystem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const exampleProjects = [
  "Je souhaite poser du carrelage dans une douche italienne de 4 m²",
  "Installer une cuisine équipée dans un appartement de 80 m²",
  "Refaire l'électricité d'une chambre de 12 m²",
  "Peindre un plafond de salon de 25 m²",
  "Remplacer un radiateur dans une chambre"
];

// Max file size in bytes (2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;

export default function WorkRecipes() {
  const { isPremium, getAuthHeaders } = useAuth();
  const { startLoading, stopLoading } = useLeonLoading();
  const location = useLocation();
  const { recipeId } = useParams(); // Get recipe ID from URL
  
  // Ad Gate - vidéo pub avant génération (users gratuits)
  const { requireAd, AdGateModal, isAdShowing } = useAdGate('recipe');
  
  const [projectDescription, setProjectDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [error, setError] = useState(null);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [loadingPending, setLoadingPending] = useState(false);
  
  // Lead popup state
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);
  
  // Photos state
  const [photos, setPhotos] = useState([]);
  const [photoError, setPhotoError] = useState(null);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [viewerPhotoIndex, setViewerPhotoIndex] = useState(0);

  // Tutoriel Léon
  const {
    config: tutorialConfig,
    showWelcome,
    showTutorial,
    startTutorial,
    dismissWelcome,
    completeTutorial,
    closeTutorial,
    restartTutorial,
    disableAllTutorials
  } = useTutorial('recipes');

  // State pour le contexte diagnostic
  const [fromDiagnostic, setFromDiagnostic] = useState(false);

  // Check if tutorial was requested via URL
  useEffect(() => {
    if (location.state?.startTutorial) {
      restartTutorial();
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Charger le contexte du diagnostic si disponible
  useEffect(() => {
    const loadDiagnosticContext = () => {
      try {
        const contextStr = sessionStorage.getItem('leon_recipe_context');
        if (contextStr) {
          const context = JSON.parse(contextStr);
          
          // Vérifier que le contexte est récent (moins de 30 minutes)
          const contextTime = new Date(context.timestamp);
          const now = new Date();
          const diffMinutes = (now - contextTime) / 1000 / 60;
          
          if (diffMinutes < 30 && context.fromDiagnostic && context.description) {
            setProjectDescription(context.description);
            setFromDiagnostic(true);
            
            // Supprimer le contexte après utilisation
            sessionStorage.removeItem('leon_recipe_context');
          }
        }
      } catch (err) {
        console.log('Erreur chargement contexte diagnostic:', err);
      }
    };
    
    // Ne charger le contexte que si on n'a pas déjà une description ou une recette
    if (!projectDescription && !recipe && !recipeId) {
      loadDiagnosticContext();
    }
  }, []);

  // Load and generate pending recipe from URL parameter
  useEffect(() => {
    const loadPendingRecipe = async () => {
      if (!recipeId) return;
      
      setLoadingPending(true);
      setError(null);
      startLoading('recette_travaux');
      
      try {
        // First, try to get the recipe (might already be generated)
        const getResponse = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
          headers: getAuthHeaders(),
          credentials: 'include'
        });
        
        if (getResponse.ok) {
          const recipeData = await getResponse.json();
          
          // If already generated, show it
          if (recipeData.status === 'completed' && recipeData.recipe) {
            // Store the full object so we can access recipe.recipe in the template
            setRecipe(recipeData);
            setProjectDescription(recipeData.project_description || '');
            setLoadingPending(false);
            stopLoading();
            return;
          }
          
          // If pending, generate it
          if (recipeData.status === 'pending') {
            setProjectDescription(recipeData.project_description || '');
            
            const generateResponse = await fetch(`${API_URL}/api/recipes/${recipeId}/generate`, {
              method: 'POST',
              headers: getAuthHeaders(),
              credentials: 'include'
            });
            
            if (generateResponse.ok) {
              const generatedData = await generateResponse.json();
              // Store the full object so we can access recipe.recipe in the template
              setRecipe(generatedData);
            } else {
              const errorData = await generateResponse.json().catch(() => ({}));
              setError(errorData.detail || 'Erreur lors de la génération de la recette');
            }
          }
        } else if (getResponse.status === 404) {
          setError('Recette non trouvée');
        } else {
          setError('Erreur lors du chargement de la recette');
        }
      } catch (err) {
        setError('Erreur de connexion. Veuillez réessayer.');
      } finally {
        setLoadingPending(false);
        stopLoading();
      }
    };
    
    loadPendingRecipe();
  }, [recipeId]);

  // Photo handling functions
  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    setPhotoError(null);
    
    // Check max photos limit
    if (photos.length + files.length > 5) {
      setPhotoError('Maximum 5 photos autorisées');
      return;
    }
    
    // Process each file
    const validFiles = [];
    for (const file of files) {
      // Check file type
      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        setPhotoError('Formats acceptés : JPG, JPEG, PNG');
        continue;
      }
      
      // Check file size and resize if needed
      if (file.size > MAX_FILE_SIZE) {
        // Resize the image
        try {
          const resizedImage = await resizeImage(file);
          validFiles.push(resizedImage);
        } catch (err) {
          console.error('Error resizing image:', err);
          setPhotoError('Erreur lors du redimensionnement de l\'image');
        }
      } else {
        // Convert to base64
        const base64 = await fileToBase64(file);
        validFiles.push(base64);
      }
    }
    
    setPhotos([...photos, ...validFiles]);
    e.target.value = ''; // Reset input
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const resizeImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions (max 1200px)
        let width = img.width;
        let height = img.height;
        const maxDim = 1200;
        
        if (width > height && width > maxDim) {
          height = (height * maxDim) / width;
          width = maxDim;
        } else if (height > maxDim) {
          width = (width * maxDim) / height;
          height = maxDim;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to base64 with compression
        const base64 = canvas.toDataURL('image/jpeg', 0.8);
        resolve(base64);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  // Fonction interne de génération (appelée après la pub)
  const performGeneration = async () => {
    // Protection contre double soumission
    if (generating) return;
    
    setGenerating(true);
    setError(null);
    setDisclaimerAccepted(false);
    startLoading('recette_travaux');
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 90000); // 90s timeout
      
      const response = await fetch(`${API_URL}/api/recipes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ 
          project_description: projectDescription,
          images: photos.length > 0 ? photos : null
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // Parser la réponse UNE SEULE FOIS
      let data = {};
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        // Si le parsing échoue, on continue avec un objet vide
      }
      
      // Gérer les différents codes d'erreur
      if (response.status === 403) {
        throw new Error(data.detail || 'Accès refusé. Veuillez vous reconnecter.');
      }
      
      if (response.status === 402) {
        throw new Error('Le service IA est temporairement indisponible. Veuillez réessayer dans quelques minutes.');
      }
      
      if (response.status === 429) {
        throw new Error('Trop de demandes en cours. Veuillez patienter 30 secondes et réessayer.');
      }
      
      if (response.status === 503 || response.status === 504) {
        throw new Error(data.detail || 'Le service est temporairement surchargé. Veuillez réessayer.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Une erreur est survenue lors de la génération. Veuillez réessayer.');
      }
      
      setRecipe(data);
      
      // Show lead popup after recipe generation (only for free users)
      if (!isPremium && !leadSubmitted) {
        setTimeout(() => {
          setShowLeadPopup(true);
        }, 2000);
      }
    } catch (err) {
      // Message d'erreur user-friendly (jamais d'erreur technique brute)
      let userMessage;
      if (err.name === 'AbortError') {
        userMessage = 'La génération a pris trop de temps. Veuillez réessayer avec une description plus courte.';
      } else if (err.message.includes('fetch') || err.message.includes('network') || err.message.includes('body') || err.message.includes('clone') || err.message.includes('Response')) {
        userMessage = 'Une erreur de connexion est survenue. Veuillez vérifier votre connexion et réessayer.';
      } else {
        userMessage = err.message || 'Une erreur est survenue lors de la génération. Veuillez réessayer.';
      }
      setError(userMessage);
    } finally {
      stopLoading();
      setGenerating(false);
    }
  };

  const getDifficultyColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'facile': return 'text-green-500 bg-green-500/10';
      case 'moyen': return 'text-yellow-500 bg-yellow-500/10';
      case 'difficile': return 'text-orange-500 bg-orange-500/10';
      case 'expert': return 'text-red-500 bg-red-500/10';
      default: return 'text-zinc-500 bg-zinc-500/10';
    }
  };

  // Handler qui passe par la pub avant de générer
  const handleGenerate = () => {
    // Protection contre double soumission
    if (generating || isAdShowing) return;
    
    if (!projectDescription.trim()) {
      setError('Veuillez décrire votre projet');
      return;
    }
    
    // Passer par la pub (si user gratuit)
    requireAd(performGeneration);
  };

  return (
    <div className="min-h-screen bg-black-rich pt-20 sm:pt-24 pb-8 sm:pb-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-2">
            Recette travaux étape par étape
          </h1>
          <p className="text-zinc-400">
            Décrivez votre projet et obtenez une méthode claire, détaillée et imprimable pour réaliser vos travaux.
          </p>
        </motion.div>

        {!recipe ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Indicateur contexte diagnostic */}
            {fromDiagnostic && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl flex items-start gap-3"
              >
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Camera className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-purple-400 font-semibold text-sm">
                    Contexte importé du diagnostic photo
                  </p>
                  <p className="text-zinc-400 text-xs mt-1">
                    Léon a pré-rempli la description avec les informations de votre diagnostic. 
                    Vous pouvez la modifier si besoin.
                  </p>
                </div>
                <button
                  onClick={() => setFromDiagnostic(false)}
                  className="text-zinc-500 hover:text-white p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            <div className="glass-card p-6">
              <label className="block text-lg font-heading font-bold text-white mb-4">
                Décrivez votre projet
              </label>
              <Textarea
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="Ex: Je veux poser du carrelage dans une douche italienne de 4m²"
                className={`min-h-[120px] bg-zinc-900/50 border-zinc-800 text-white resize-none ${
                  fromDiagnostic ? 'border-purple-500/30' : ''
                }`}
                data-testid="recipe-description-input"
              />
              
              {!fromDiagnostic && (
                <div className="mt-4">
                  <p className="text-zinc-500 text-sm mb-2">Exemples de projets :</p>
                  <div className="flex flex-wrap gap-2">
                    {exampleProjects.map((example, i) => (
                      <button
                        key={i}
                        onClick={() => setProjectDescription(example)}
                        className="px-3 py-1 bg-zinc-800/50 rounded-full text-xs text-zinc-400 hover:text-gold-500 hover:bg-gold-500/10 transition-all"
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Photo upload section */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="block text-lg font-heading font-bold text-white">
                    Ajoutez des photos du chantier
                  </label>
                  <p className="text-zinc-500 text-sm mt-1">
                    Aidez Léon à comprendre la situation réelle (optionnel, 1 à 5 photos max)
                  </p>
                </div>
                <div className="flex items-center gap-2 text-zinc-400 text-sm">
                  <Camera className="w-4 h-4" />
                  <span>{photos.length}/5</span>
                </div>
              </div>
              
              {/* Photo preview grid */}
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 mb-4">
                  {photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-zinc-700 hover:border-gold-500/50 transition-all">
                      <img 
                        src={photo} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          onClick={() => { setViewerPhotoIndex(index); setShowPhotoViewer(true); }}
                          className="p-2 bg-white/20 rounded-full hover:bg-white/30 transition-colors"
                        >
                          <Eye className="w-4 h-4 text-white" />
                        </button>
                        <button
                          onClick={() => removePhoto(index)}
                          className="p-2 bg-red-500/80 rounded-full hover:bg-red-500 transition-colors"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                      </div>
                      <span className="absolute bottom-1 right-1 px-2 py-0.5 bg-black/70 rounded text-xs text-white">
                        {index + 1}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Upload avec MobilePhotoUpload */}
              {photos.length < 5 && (
                <MobilePhotoUpload
                  onImageSelect={(file, preview) => {
                    if (photos.length < 5) {
                      setPhotos([...photos, preview]);
                      setPhotoError(null);
                    }
                  }}
                  onImageRemove={() => {}}
                  currentImage={null}
                  label={photos.length === 0 
                    ? 'Ajoutez des photos du chantier' 
                    : `Ajouter ${5 - photos.length} photo(s) supplémentaire(s)`
                  }
                  variant="default"
                  cameraFirst={true}
                  maxSize={2}
                  disabled={generating || photos.length >= 5}
                />
              )}
              
              {/* Photo error */}
              {photoError && (
                <p className="text-red-400 text-sm mt-3 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {photoError}
                </p>
              )}
              
              {/* Leon tip about photos */}
              {photos.length > 0 && (
                <div className="mt-4 p-3 bg-gold-500/10 border border-gold-500/20 rounded-lg">
                  <p className="text-gold-400 text-sm flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>
                      Léon va analyser vos {photos.length} photo(s) pour identifier les matériaux, 
                      évaluer l'état du support et adapter la recette à votre situation réelle.
                    </span>
                  </p>
                </div>
              )}
            </div>

            {error && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-amber-400 font-medium mb-1">Oups ! Un petit souci</p>
                    <p className="text-amber-300/80 text-sm">{error}</p>
                    <div className="flex gap-4 mt-3">
                      <button
                        onClick={() => { 
                          setError(null); 
                          setGenerating(false);
                          // Petit délai pour s'assurer que le state est reset
                          setTimeout(() => handleGenerate(), 100);
                        }}
                        className="text-sm text-gold-500 hover:text-gold-400 underline font-medium"
                        data-testid="recipe-retry-btn"
                      >
                        Réessayer maintenant
                      </button>
                      <button
                        onClick={() => { 
                          setError(null); 
                          setGenerating(false);
                          setProjectDescription('');
                          setPhotos([]);
                        }}
                        className="text-sm text-zinc-400 hover:text-zinc-300 underline"
                      >
                        Recommencer
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={generating || isAdShowing || !projectDescription.trim()}
              className="w-full py-6 bg-gold-500 hover:bg-gold-400 text-black font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="recipe-submit-btn"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  <BookOpen className="w-5 h-5 mr-2" />
                  Générer la recette
                </>
              )}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Header */}
            <div className="glass-card p-6">
              <h2 className="text-2xl font-heading font-bold text-white mb-4">
                {recipe.recipe?.title || 'Recette travaux'}
              </h2>
              <div className="flex flex-wrap gap-4">
                {recipe.recipe?.difficulty_level && (
                  <div className={`px-4 py-2 rounded-lg ${getDifficultyColor(recipe.recipe.difficulty_level)}`}>
                    Difficulté: {recipe.recipe.difficulty_level}
                  </div>
                )}
                {recipe.recipe?.estimated_duration && (
                  <div className="px-4 py-2 rounded-lg bg-zinc-800/50 text-zinc-300 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {recipe.recipe.estimated_duration}
                  </div>
                )}
                {recipe.has_images && (
                  <div className="px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    {recipe.images_count} photo(s) analysée(s)
                  </div>
                )}
              </div>
            </div>

            {/* Photos submitted - Display in result */}
            {recipe.images && recipe.images.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-gold-500" />
                  Photos du chantier
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {recipe.images.map((photo, index) => (
                    <button
                      key={index}
                      onClick={() => { setViewerPhotoIndex(index); setShowPhotoViewer(true); }}
                      className="relative group aspect-square rounded-xl overflow-hidden border-2 border-zinc-700 hover:border-gold-500/50 transition-all"
                    >
                      <img 
                        src={photo} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Photo Analysis - Leon's observations */}
            {recipe.recipe?.photo_analysis && (
              <div className="glass-card p-6 border border-gold-500/20">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-gold-500" />
                  Analyse visuelle de Léon
                </h3>
                <div className="space-y-4">
                  {recipe.recipe.photo_analysis.observations && (
                    <div className="p-4 bg-zinc-800/50 rounded-xl">
                      <p className="text-gold-400 text-sm font-medium mb-2">Ce que j'ai observé :</p>
                      <p className="text-zinc-300">{recipe.recipe.photo_analysis.observations}</p>
                    </div>
                  )}
                  {recipe.recipe.photo_analysis.impact && (
                    <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                      <p className="text-emerald-400 text-sm font-medium mb-2">Impact sur mes recommandations :</p>
                      <p className="text-zinc-300">{recipe.recipe.photo_analysis.impact}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Steps */}
            {recipe.recipe?.steps && recipe.recipe.steps.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-gold-500" />
                  Étapes
                </h3>
                <div className="space-y-4">
                  {recipe.recipe.steps.map((step, i) => (
                    <div key={i} className="p-4 bg-zinc-800/50 rounded-xl">
                      <div className="flex items-start gap-4">
                        <div className="w-8 h-8 rounded-full bg-gold-500/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-gold-500 font-bold">{step.step_number || i + 1}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-medium mb-2">{step.title}</h4>
                          <p className="text-zinc-400 text-sm">{step.description}</p>
                          {step.duration && (
                            <p className="text-zinc-500 text-xs mt-2 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> {step.duration}
                            </p>
                          )}
                          {step.tips && step.tips.length > 0 && (
                            <div className="mt-3 p-3 bg-gold-500/10 rounded-lg">
                              <p className="text-gold-500 text-xs font-medium mb-1">Conseils :</p>
                              <ul className="text-zinc-300 text-xs space-y-1">
                                {step.tips.map((tip, j) => (
                                  <li key={j}>• {tip}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Materials */}
            {recipe.recipe?.materials && recipe.recipe.materials.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5 text-gold-500" />
                  Matériaux nécessaires
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {recipe.recipe.materials.map((material, i) => (
                    <div key={i} className="p-3 bg-zinc-800/50 rounded-lg flex items-center justify-between">
                      <span className="text-zinc-300">{material.name}</span>
                      <div className="text-right">
                        <span className="text-zinc-500 text-sm">{material.quantity}</span>
                        {material.price_estimate && (
                          <p className="text-gold-500 text-xs">{material.price_estimate}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tools */}
            {recipe.recipe?.tools && recipe.recipe.tools.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-gold-500" />
                  Outils nécessaires
                </h3>
                <div className="flex flex-wrap gap-2">
                  {recipe.recipe.tools.map((tool, i) => (
                    <span 
                      key={i} 
                      className={`px-3 py-1 rounded-full text-sm ${
                        tool.essential 
                          ? 'bg-gold-500/20 text-gold-500 border border-gold-500/30' 
                          : 'bg-zinc-800/50 text-zinc-400'
                      }`}
                    >
                      {tool.name} {tool.essential && '★'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Common Mistakes */}
            {recipe.recipe?.common_mistakes && recipe.recipe.common_mistakes.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Erreurs à éviter
                </h3>
                <div className="space-y-3">
                  {recipe.recipe.common_mistakes.map((mistake, i) => {
                    // Handle both string format and object format
                    const isString = typeof mistake === 'string';
                    const mistakeText = isString ? mistake : (mistake.mistake || mistake.text || mistake.label || '');
                    const howToAvoid = isString ? null : mistake.how_to_avoid;
                    
                    return (
                      <div key={i} className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-red-400 font-medium">{mistakeText}</p>
                        {howToAvoid && (
                          <p className="text-zinc-400 text-sm mt-1">{howToAvoid}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Safety Tips */}
            {recipe.recipe?.safety_tips && recipe.recipe.safety_tips.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-500" />
                  Conseils de sécurité
                </h3>
                <ul className="space-y-2">
                  {recipe.recipe.safety_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-3 text-zinc-300">
                      <span className="text-green-500">✓</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Disclaimer Checkbox and Print Button */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="text-xl font-heading font-bold text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold-500" />
                Rapport imprimable
              </h3>
              
              {/* Disclaimer checkbox */}
              <button
                onClick={() => setDisclaimerAccepted(!disclaimerAccepted)}
                className="flex items-start gap-3 text-left w-full p-4 bg-zinc-800/50 rounded-xl border border-zinc-700/50 hover:border-gold-500/30 transition-all"
                data-testid="recipe-disclaimer-checkbox"
              >
                <div className="flex-shrink-0 mt-0.5">
                  {disclaimerAccepted ? (
                    <CheckSquare className="w-5 h-5 text-gold-500" />
                  ) : (
                    <Square className="w-5 h-5 text-zinc-500" />
                  )}
                </div>
                <span className="text-zinc-300 text-sm leading-relaxed">
                  Je reconnais que cette recette travaux constitue une aide à la décision et une méthode indicative, 
                  et qu'elle ne remplace pas l'avis, le contrôle ou l'intervention d'un professionnel qualifié.
                </span>
              </button>

              {/* Print report button */}
              {recipe.recipe_id && (
                <Link
                  to={disclaimerAccepted ? `/recipe-report/${recipe.recipe_id}` : '#'}
                  onClick={(e) => !disclaimerAccepted && e.preventDefault()}
                  className={`flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold transition-all ${
                    disclaimerAccepted
                      ? 'bg-gold-500 hover:bg-gold-400 text-black cursor-pointer'
                      : 'bg-zinc-700/50 text-zinc-500 cursor-not-allowed'
                  }`}
                  data-testid="recipe-open-report-btn"
                >
                  <Printer className="w-5 h-5" />
                  Ouvrir le rapport imprimable
                </Link>
              )}
              
              {!disclaimerAccepted && recipe.recipe_id && (
                <p className="text-zinc-500 text-xs text-center">
                  Cochez la case ci-dessus pour accéder au rapport imprimable
                </p>
              )}
            </div>

            <Button
              onClick={() => { setRecipe(null); setProjectDescription(''); setDisclaimerAccepted(false); setPhotos([]); }}
              variant="outline"
              className="w-full border-gold-500/30 text-gold-500 hover:bg-gold-500/10"
              data-testid="recipe-new-btn"
            >
              Nouvelle recette
            </Button>

            {/* Lead Capture CTA - "Recevoir des devis d'artisans" */}
            <LeadCaptureCTA
              analysisType="recipe"
              analysisId={recipe?.recipe_id}
              projectSummary={recipe?.recipe?.title || projectDescription || "Recette travaux"}
            />
          </motion.div>
        )}
      </div>

      <LeonMascot 
        message={recipe 
          ? "Votre recette est prête ! Suivez les étapes dans l'ordre et n'hésitez pas à me poser des questions si vous bloquez."
          : "Décrivez votre projet de travaux et je génère une recette complète avec toutes les étapes, matériaux et conseils."
        }
      />
      
      {/* Tutorial Welcome Card */}
      <AnimatePresence>
        {showWelcome && !recipe && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <TutorialWelcomeCard 
              config={tutorialConfig}
              onStart={startTutorial} 
              onDismiss={dismissWelcome}
              onDisableTutorials={disableAllTutorials}
            />
          </div>
        )}
      </AnimatePresence>
      
      {/* Leon Tutorial Modal */}
      <LeonTutorial 
        isOpen={showTutorial}
        onClose={closeTutorial}
        onComplete={completeTutorial}
        config={tutorialConfig}
      />
      
      {/* Photo Viewer Modal */}
      <AnimatePresence>
        {showPhotoViewer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
            onClick={() => setShowPhotoViewer(false)}
          >
            {/* Close button */}
            <button
              onClick={() => setShowPhotoViewer(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Photo counter */}
            <div className="absolute top-4 left-4 text-white/70 text-sm">
              Photo {viewerPhotoIndex + 1} / {(recipe?.images || photos).length}
            </div>
            
            {/* Navigation arrows */}
            {(recipe?.images || photos).length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const photoArray = recipe?.images || photos;
                    setViewerPhotoIndex((prev) => (prev - 1 + photoArray.length) % photoArray.length);
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors"
                >
                  <svg className="w-8 h-8 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const photoArray = recipe?.images || photos;
                    setViewerPhotoIndex((prev) => (prev + 1) % photoArray.length);
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 rounded-full bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors"
                >
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
            
            {/* Main image */}
            <motion.img
              key={viewerPhotoIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              src={(recipe?.images || photos)[viewerPhotoIndex]}
              alt={`Photo ${viewerPhotoIndex + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Thumbnails */}
            {(recipe?.images || photos).length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-zinc-900/80 p-2 rounded-lg">
                {(recipe?.images || photos).map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewerPhotoIndex(idx);
                    }}
                    className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${
                      idx === viewerPhotoIndex ? 'border-gold-500' : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={photo} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Ad Modal (before generation for free users) */}
      <AdGateModal />

      {/* Lead Interest Popup (after recipe generation for free users) */}
      <LeadInterestPopup
        isOpen={showLeadPopup}
        onClose={() => setShowLeadPopup(false)}
        analysisType="recipe"
        analysisId={recipe?.recipe_id}
        projectSummary={projectDescription || recipe?.recipe?.title || "Recette travaux"}
        analysisContent={recipe?.recipe?.description}
        detectedCategory={recipe?.recipe?.work_type || null}
        onLeadSubmitted={() => {
          setLeadSubmitted(true);
          setShowLeadPopup(false);
        }}
      />

      {/* Sticky Banner Ad (bottom, for free users) */}
      {!isPremium && !generating && <BannerAd placement="bottom" sticky={true} />}
    </div>
  );
}
